import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { TokenService } from '../services/token.service';
import { AuthService } from '../services/auth.service';
import { OauthService } from '../services/oauth.service';

@Injectable({
  providedIn: 'root',
})
export class TokenInterceptor implements HttpInterceptor {
  private apiUrl: string = environment.laravel_url + '/api';
  private authUrl: string = environment.auth_url;

  private token = inject(TokenService);
  private auth = inject(AuthService);
  private oauth = inject(OauthService);
  private router = inject(Router);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const is_api = request.url.indexOf(this.apiUrl) !== -1;
    const is_token_endpoint = request.url.indexOf(`${this.authUrl}/oauth/token`) !== -1;
    const is_logout_endpoint = is_api && request.url.indexOf('/auth/logout') !== -1;

    if (is_api && this.token.hasAttachableAccessToken()) {
      request = request.clone({
        headers: request.headers.set('Authorization', 'Bearer ' + this.token.get()),
      });
    }

    return next.handle(request).pipe(
      catchError((error: unknown) => {
        const should_skip_token_refresh =
          !(error instanceof HttpErrorResponse) ||
          error.status !== 401 ||
          is_token_endpoint ||
          is_logout_endpoint ||
          !is_api ||
          this.auth.isLoggingOut();

        if (should_skip_token_refresh) {
          return throwError(() => error);
        }

        return this.auth.refreshToken().pipe(
          switchMap(ok => {
            if (ok) {
              return next.handle(this.withBearer(request));
            }

            if (this.auth.isImpersonating()) {
              this.auth.desimpersonate();

              if (this.token.hasAttachableAccessToken()) {
                return next.handle(this.withBearer(request));
              }
            }

            const redirect = this.router.url;
            if (redirect && !redirect.startsWith('/auth')) {
              this.oauth.rememberPostLoginRedirect(redirect);
            }

            this.auth.logout({ expired: '1', redirect });
            return throwError(() => error);
          })
        );
      })
    );
  }

  private withBearer(request: HttpRequest<any>): HttpRequest<any> {
    return request.clone({
      headers: request.headers.set('Authorization', 'Bearer ' + this.token.get()),
    });
  }
}
